import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { MobileBottomSheet } from "@/components/general/mobile-bottom-sheet/MobileBottomSheet";
import { Button, Icon } from "@pepperstone/pepper-ui-kit";
import { classNames } from "@repo/ui";

import { AccountItem } from "../account-item/AccountItem";
import styles from "./MobileAccountSelector.module.css";

import type { CashierTradingAccountMargin } from "@/api/cashier";

const CLOSE_ANIMATION_MS = 300;

type MobileAccountSelectorProps = {
  accounts: CashierTradingAccountMargin[];
  amountField?: "margin" | "balance";
  selectedAccount?: CashierTradingAccountMargin | null;
  onAccountChange: (newAccount: CashierTradingAccountMargin) => void;
  onClose: () => void;
  "data-testid": string;
};

export const MobileAccountSelector = ({
  accounts,
  amountField = "margin",
  selectedAccount,
  onAccountChange,
  onClose,
  "data-testid": testId,
}: MobileAccountSelectorProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [account, setAccount] = useState<CashierTradingAccountMargin | null>(
    selectedAccount ?? null
  );
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const openFrame = requestAnimationFrame(() => setIsOpen(true));
    return () => {
      cancelAnimationFrame(openFrame);
      clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      closeTimeoutRef.current = setTimeout(onClose, CLOSE_ANIMATION_MS);
    }
  };

  const handleSelectAccount = () => {
    if (!account) {
      return;
    }
    onAccountChange(account);
    handleOpenChange(false);
  };

  const renderAccounts = () => {
    return accounts.map((item) => {
      const isSelected = account?.id === item.id;
      return (
        <div
          key={item.id}
          className={classNames([
            styles["drop-down-option-wrapper"],
            isSelected && styles.selected,
          ])}
        >
          <AccountItem
            data-testid={`${testId}-account-${item.id}`}
            account={item}
            onClick={() => setAccount(item)}
            amountField={amountField}
          />
          {isSelected && <Icon icon="check-mark" color="primary" />}
        </div>
      );
    });
  };

  return (
    <MobileBottomSheet
      isOpen={isOpen}
      isDisabled={false}
      canOpen={true}
      onOpenChange={handleOpenChange}
    >
      <div className={styles["account-list-container"]}>{renderAccounts()}</div>
      <Button
        disabled={!account}
        type="button"
        onClick={() => handleSelectAccount()}
        variant="ps-btn-primary"
      >
        {t("global.buttons.confirm")}
      </Button>
    </MobileBottomSheet>
  );
};
